import "swiper/css";
import "swiper/swiper-bundle.css";
import { Swiper, SwiperClass, SwiperRef, SwiperSlide } from "swiper/react";
import { useEffect, useRef, useState } from "react";
import { Images } from "@assets/images";
import { Book } from "@app/types";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { RoutePath } from "@app/constants";

interface SliderPerViewProps {
  books: Book[];
}

function SliderPerView({ books }: SliderPerViewProps) {
  const swiperRef = useRef<SwiperRef>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    if (swiperRef.current && books.length >= 3) {
      setActiveIndex(1);
      swiperRef.current.swiper.slideTo(1);
    }
  }, [books]);

  const goNext = () => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slideNext();

      if (!swiperRef.current.swiper.isEnd) {
        setActiveIndex(activeIndex + 1);
      }
    }
  };

  const handleClick = (bookId: number) => {
    navigate(`/${RoutePath.BOOK_DETAIL}/${bookId}`);
  };

  const goPrev = () => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slidePrev();

      if (!swiperRef.current.swiper.isBeginning) {
        setActiveIndex(activeIndex - 1);
      }
    }
  };

  const handleSlideChange = (swiper: SwiperClass) => {
    setActiveIndex(swiper.activeIndex);
  };

  return (
    <div className="flex items-center justify-center gap-9">
      {!!books.length && (
        <>
          <button onClick={goPrev}>
            <img src={Images.LeftArrowIcon.default} alt="" />
          </button>
          <Swiper
            ref={swiperRef}
            className="[&_.swiper-wrapper]:items-center"
            onSlideChange={handleSlideChange}
            breakpoints={{
              500: {
                slidesPerView: 2,
                spaceBetween: 15,
              },
              768: {
                slidesPerView: 3,
                spaceBetween: 15,
              },
            }}
            centeredSlides
            slideToClickedSlide
            speed={500}
          >
            {!!books.length &&
              books.map((item, index) => (
                <SwiperSlide
                  key={`${item.id}-${index}`}
                  className="flex justify-center"
                >
                  <div
                    className={clsx(
                      "relative shadow-lg bg-contain rounded-xl overflow-hidden cursor-pointer",
                      "h-[600px] w-[400px] max-2xl:h-[500px] max-2xl:w-[400px] max-lg:h-[300px] max-lg:w-[250px]",
                      "max-xl:h-[440px] max-xl:w-[400px]",
                      {
                        "!h-[400px] !w-[270px] max-2xl:!h-[350px] max-2xl:!w-[240px] max-lg:!h-[190px] max-lg:!w-[130px]":
                          activeIndex !== index,
                        "max-xl:!h-[250px] max-xl:!w-[170px]":
                          activeIndex !== index,
                      }
                    )}
                    style={{
                      transition: "transform 0.9 s ease-in-out",
                    }}
                    onClick={() => handleClick(item.id)}
                  >
                    <div
                      className="absolute inset-0 bg-contain bg-no-repeat bg-center"
                      style={{ backgroundImage: `url(${item.thumbnails[0]})` }}
                    />
                    <div className="absolute inset-0 bg-slate-500 opacity-10 group-hover:opacity-50" />
                  </div>
                </SwiperSlide>
              ))}
          </Swiper>
          <button onClick={goNext}>
            <img src={Images.RightArrowIcon.default} alt="" />
          </button>
        </>
      )}
    </div>
  );
}

export default SliderPerView;
